// Continuous same-parent runs across a rotation.
//
// A transition only exists where the PARENT changes. When two blocks of the
// same parent meet exactly (one ends where the next begins), that seam is an
// artefact of how the schedule was entered, not a handover: a hand-built
// schedule can do it, and so can data saved before a block could span more
// than one week (the Every Other Weekend template once stored the weekday
// parent's 11-day stretch as three blocks). Presenting such a seam as
// "Aaron drops off the Children" would be wrong on an agreement attachment.
//
// This module groups blocks into runs so the grid can give each run ONE
// summary sentence, ONE transition (the real one, at the run's end), and
// label the continuation blocks as such. It is purely a presentation grouping;
// the stored blocks are untouched, and editing still works block by block.

import { DAY_INDEX, MIN_PER_DAY, MIN_PER_WEEK, timeToMin } from './weekMath.js'

// Flat cycle minutes, mirroring rotation.js.
function flatten(e, wi) {
  const start = (wi + (e.startWeekOffset || 0)) * MIN_PER_WEEK
    + (DAY_INDEX[e.startDay] ?? 0) * MIN_PER_DAY + timeToMin(e.startTime)
  let end = (wi + (e.endWeekOffset || 0)) * MIN_PER_WEEK
    + (DAY_INDEX[e.endDay] ?? 0) * MIN_PER_DAY + timeToMin(e.endTime)
  if (end <= start) end += MIN_PER_WEEK
  return { start, end }
}

// continuousRuns(weeks)
// → {
//     byKey: Map<'wi:ei', { isHead, isContinuation, run }>,
//     runs:  [ { partner, head: { wi, ei, e }, last: { wi, ei, e }, blocks: ['wi:ei', …], totalMins } ]
//   }
export function continuousRuns(weeks) {
  const list = weeks || []
  const cycle = list.length * MIN_PER_WEEK
  const items = []
  list.forEach((w, wi) => {
    ;(w?.events || []).forEach((e, ei) => {
      const { start, end } = flatten(e, wi)
      const shift = cycle ? Math.floor(start / cycle) * cycle : 0
      items.push({ key: `${wi}:${ei}`, wi, ei, e, start: start - shift, end: end - shift, partner: e.partner })
    })
  })
  items.sort((a, b) => a.start - b.start)

  const byKey = new Map()
  const runs = []
  const n = items.length
  if (!n) return { byKey, runs }

  // A block continues the previous one when the same parent's block ends
  // exactly where it starts. Item 0 checks the last item across the seam.
  for (let i = 0; i < n; i++) {
    const cur = items[i]
    const prev = items[(i - 1 + n) % n]
    const prevEnd = i === 0 ? prev.end - cycle : prev.end
    cur.isContinuation = n > 1 && prev.partner === cur.partner && prevEnd === cur.start
  }
  // Every block continuing the one before means one parent holds the whole
  // cycle; give the loop a start at the earliest block.
  if (!items.some((x) => !x.isContinuation)) items[0].isContinuation = false

  for (let i = 0; i < n; i++) {
    const head = items[i]
    if (head.isContinuation) continue
    const blocks = [head]
    for (let s = 1; s < n; s++) {
      const next = items[(i + s) % n]
      if (!next.isContinuation) break
      blocks.push(next)
    }
    const last = blocks[blocks.length - 1]
    const run = {
      partner: head.partner,
      head: { wi: head.wi, ei: head.ei, e: head.e },
      last: { wi: last.wi, ei: last.ei, e: last.e },
      blocks: blocks.map((b) => b.key),
      totalMins: blocks.reduce((t, b) => t + (b.end - b.start), 0),
    }
    runs.push(run)
    blocks.forEach((b, idx) => byKey.set(b.key, { isHead: idx === 0, isContinuation: idx > 0, run }))
  }
  return { byKey, runs }
}
